// Safe DOM construction for the dashboard's reusable components.
//
// The existing views build markup with template strings and innerHTML, which
// review finding P1 ("Stored HTML can enter the dashboard") flags: client names
// and template names are interpolated unescaped. Everything under src/ui and
// src/preview goes through h() instead. Strings become text nodes, attributes go
// through setAttribute, and there is deliberately no way to pass raw HTML.

export type Child = Node | string | number | null | undefined | false | readonly Child[];

type Listeners = { [K in keyof HTMLElementEventMap]?: (ev: HTMLElementEventMap[K]) => void };

export interface Props {
  class?: string | false | null;
  dataset?: Record<string, string | number | boolean | undefined>;
  on?: Listeners;
  /** CSS custom properties / properties set through CSSOM, never a style attribute string. */
  css?: Record<string, string>;
  [attr: string]: unknown;
}

const FORBIDDEN_ATTRS = new Set(["innerhtml", "outerhtml", "srcdoc", "style"]);
const URL_ATTRS = new Set(["href", "src", "action", "formaction", "xlink:href"]);
const SAFE_SCHEMES = new Set(["http:", "https:", "mailto:", "tel:"]);

/** Allows in-app hash links, root-relative paths and http(s)/mailto/tel. Everything else becomes "#". */
export function safeUrl(value: string): string {
  const v = value.trim();
  if (v === "") return "#";
  if (v.startsWith("#") || v.startsWith("?") || /^\/(?!\/)/.test(v)) return v;
  try {
    return SAFE_SCHEMES.has(new URL(v).protocol) ? v : "#";
  } catch {
    return "#";
  }
}

function applyProps(el: Element, props?: Props | null) {
  if (!props) return;
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === "class") {
      el.setAttribute("class", String(value));
    } else if (key === "dataset") {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (v !== undefined) (el as HTMLElement).dataset[k] = String(v);
      }
    } else if (key === "on") {
      for (const [type, fn] of Object.entries(value as Record<string, EventListener>)) {
        el.addEventListener(type, fn);
      }
    } else if (key === "css") {
      for (const [k, v] of Object.entries(value as Record<string, string>)) {
        // setProperty needs kebab-case; accept camelCase (flexWrap) for convenience.
        (el as HTMLElement).style.setProperty(k.startsWith("--") ? k : k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`), v);
      }
    } else {
      const lower = key.toLowerCase();
      if (lower.startsWith("on")) throw new Error(`Inline handler attribute "${key}" is not allowed; use props.on`);
      if (FORBIDDEN_ATTRS.has(lower)) throw new Error(`Attribute "${key}" is not allowed in h()`);
      const text = value === true ? "" : String(value);
      el.setAttribute(key, URL_ATTRS.has(lower) ? safeUrl(text) : text);
    }
  }
}

function append(parent: Node, children: readonly Child[]) {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    if (Array.isArray(child)) append(parent, child);
    else if (child instanceof Node) parent.appendChild(child);
    else parent.appendChild(document.createTextNode(String(child)));
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  applyProps(el, props);
  append(el, children);
  return el;
}

const SVG_NS = "http://www.w3.org/2000/svg";

export function svgEl(tag: string, attrs?: Record<string, string | number>, ...children: Node[]): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs ?? {})) el.setAttribute(k, String(v));
  for (const c of children) el.appendChild(c);
  return el;
}

/** Replaces all children of `parent` with `children`. */
export function mount(parent: Element, ...children: Child[]) {
  parent.replaceChildren();
  append(parent, children);
}

let uidCounter = 0;
export function uid(prefix = "cp"): string {
  uidCounter += 1;
  return `${prefix}-${uidCounter}`;
}

/** Text that is available to assistive technology but not painted. */
export function srOnly(text: string): HTMLSpanElement {
  return h("span", { class: "cp-sr-only" }, text);
}
